import { httpService } from '../services/httpService';

import '../contracts/Orders_AgendaMensalResponse';
import type { Orders_AgendaMensalResponseDados } from '../contracts/Orders_AgendaMensalResponse';
import type { Orders_AgendaMensalItemRow } from '../contracts/Orders_AgendaMensalItemRow';

/**
 * Composable for the monthly agenda calendar view.
 *
 * Owns the GET to the SO agenda_mensal endpoint and wires the typed
 * `Orders_AgendaMensalResponse` ResponseContract.
 *
 * The endpoint returns PENDING order items grouped by expected delivery
 * day within a given year+month.
 *
 * Also exposes pure helpers used by PDVAgendaMensalPage:
 *   - getUrgency / urgencyDaysClass — card colour classification
 *   - buildMonthCells — 42-cell calendar grid with correct year-boundary dates
 */

export interface MonthCell {
  key: string;
  day: number;
  dateStr: string;
  isToday: boolean;
  isCurrentMonth: boolean;
  items: Orders_AgendaMensalItemRow[];
}

/** Day entries as returned by Orders_AgendaMensalResponseDados.days */
export type AgendaMensalDay = Orders_AgendaMensalResponseDados['days'][number];

export type Urgency = 'overdue' | 'today' | 'tomorrow' | 'upcoming' | 'rescheduled';

/** Format a Date as YYYY-MM-DD without timezone shift (local calendar date). */
function toYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Classify an item's urgency based on days_until_due.
 *
 * Pure function — unit-testable without Vue.
 */
export function getUrgency(item: Orders_AgendaMensalItemRow): Urgency {
  // Rescheduled items take priority — show in gray regardless of due date
  if (item.so_work_order_step_was_resumed === 1) return 'rescheduled';

  const d = item.days_until_due;
  if (d === null || d === undefined) return 'upcoming';
  if (d < 0) return 'overdue';
  if (d === 0) return 'today';
  if (d === 1) return 'tomorrow';
  return 'upcoming';
}

/** CSS class derived from urgency. */
export function urgencyDaysClass(item: Orders_AgendaMensalItemRow): string {
  return 'urgency-' + getUrgency(item);
}

/**
 * Build the 42-cell (6 × 7) calendar grid for a given year/month.
 *
 * Spill cells for the previous and next months use real Date arithmetic
 * so they are correct at year boundaries (e.g. month=1 rolls to year-1/December,
 * month=12 rolls to year+1/January).
 *
 * Pure function — unit-testable without Vue.
 */
export function buildMonthCells(
  year: number,
  month: number,
  daysByDay: Record<number, Orders_AgendaMensalItemRow[]>,
): MonthCell[] {
  const todayStr = new Date().toDateString();
  // First weekday (0=Sun) of this month, and number of days in this month.
  const firstWeekday = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();

  const cells: MonthCell[] = [];

  // Prev-month spill cells — use real Date so year boundary is handled.
  for (let i = firstWeekday - 1; i >= 0; i--) {
    const date = new Date(year, month - 1, -i); // day 0 = last day of prev month, day -1 = 2nd-to-last, …
    cells.push({
      key: 'p' + date.getDate(),
      day: date.getDate(),
      dateStr: toYmd(date),
      isToday: false,
      isCurrentMonth: false,
      items: [],
    });
  }

  // Current-month cells.
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month - 1, d);
    cells.push({
      key: 'c' + d,
      day: d,
      dateStr: toYmd(date),
      isToday: date.toDateString() === todayStr,
      isCurrentMonth: true,
      items: daysByDay[d] || [],
    });
  }

  // Next-month spill cells — use real Date so year boundary is handled.
  const rem = 42 - cells.length;
  for (let d = 1; d <= rem; d++) {
    const date = new Date(year, month, d); // month (0-based) = month+1 (1-based) of same year
    cells.push({
      key: 'n' + d,
      day: date.getDate(),
      dateStr: toYmd(date),
      isToday: false,
      isCurrentMonth: false,
      items: [],
    });
  }

  return cells;
}

export function useMonthlyAgenda() {
  const basePath = '/index.php/so/orders';

  const getAgendaMensal = async (
    year: number,
    month: number,
  ): Promise<Orders_AgendaMensalResponseDados | null> => {
    const response: any = await httpService.get(`${basePath}/agenda_mensal`, {
      params: { year, month },
      responseContract: 'Orders_AgendaMensalResponse',
    });
    if (response.success && response.dados) {
      return response.dados;
    }
    return null;
  };

  return {
    getAgendaMensal,
    getUrgency,
    urgencyDaysClass,
    buildMonthCells,
  };
}
